import { stringifyEntityRef } from '@backstage/catalog-model';
import { Progress } from '@backstage/core-components';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { makeStyles } from 'tss-react/mui';
import { useEffect, useState } from 'react';
import { useLeaves } from '../api/useLeaves';
import type { TeamMember } from '../data/model';
import { useGroupMembers } from '../hooks/useGroupMembers';
import { fonts, themeTokens } from '../theme';
import { LeaveTrackerPage } from './LeaveTrackerPage';

const useStyles = makeStyles()(theme => {
  const palette = themeTokens(theme);
  return {
    denied: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 12,
      textAlign: 'center',
      padding: '64px 24px',
      color: palette.muted,
      fontFamily: fonts.body,
      '& svg': { fontSize: 40, color: palette.terracotta },
    },
    deniedTitle: {
      fontSize: 18,
      fontWeight: 700,
      color: palette.navy,
    },
    deniedBody: { fontSize: 14, maxWidth: 420 },
  };
});

/**
 * Loads persisted leaves for the group from the backend and renders the
 * tracker. Split out so the data hook only runs once membership is confirmed.
 */
const LeaveTrackerDataView = ({
  groupRef,
  members,
  currentUserRef,
}: {
  groupRef: string;
  members: TeamMember[];
  currentUserRef: string;
}) => {
  const { leaves, loading, error, addLeave, updateLeave, removeLeave } =
    useLeaves(
      groupRef,
      members.map(member => member.id),
    );

  if (loading) {
    return <Progress />;
  }

  return (
    <LeaveTrackerPage
      members={members}
      leaves={error ? [] : leaves}
      onAddLeave={addLeave}
      onUpdateLeave={updateLeave}
      onDeleteLeave={removeLeave}
      currentUserRef={currentUserRef}
    />
  );
};

/**
 * Renders the Team Leave Tracker only when the signed-in user is a member of
 * the group entity the tab is attached to. Membership is granted when the user
 * appears in the group's catalog members (its `hasMember` relations) or when
 * the identity's ownership refs include the group. Checking the catalog members
 * makes this robust to identity providers (such as the guest provider) that do
 * not populate group memberships in the ownership refs.
 */
export const EntityLeaveTrackerContent = () => {
  const { classes } = useStyles();
  const { entity } = useEntity();
  const identityApi = useApi(identityApiRef);
  const [identityResolved, setIdentityResolved] = useState(false);
  const [currentUserRef, setCurrentUserRef] = useState('');
  const [ownedRefs, setOwnedRefs] = useState<string[]>([]);
  const { members, loading: membersLoading } = useGroupMembers(entity);
  const groupRef = stringifyEntityRef(entity).toLocaleLowerCase('en-US');

  useEffect(() => {
    let cancelled = false;

    identityApi
      .getBackstageIdentity()
      .then(identity => {
        if (cancelled) {
          return;
        }
        setCurrentUserRef(identity.userEntityRef.toLocaleLowerCase('en-US'));
        setOwnedRefs(
          identity.ownershipEntityRefs.map(ref =>
            ref.toLocaleLowerCase('en-US'),
          ),
        );
        setIdentityResolved(true);
      })
      .catch(() => {
        if (!cancelled) {
          setIdentityResolved(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [identityApi]);

  if (!identityResolved || membersLoading) {
    return <Progress />;
  }

  const isMember =
    ownedRefs.includes(groupRef) ||
    members.some(
      member => member.id.toLocaleLowerCase('en-US') === currentUserRef,
    );

  if (!isMember) {
    return (
      <div className={classes.denied}>
        <LockOutlinedIcon />
        <div className={classes.deniedTitle}>Members only</div>
        <div className={classes.deniedBody}>
          The Team Leave Tracker is only visible to members of{' '}
          {entity.metadata.title ?? entity.metadata.name}. Ask a group owner to
          add you if you should have access.
        </div>
      </div>
    );
  }

  return (
    <LeaveTrackerDataView
      groupRef={groupRef}
      members={members}
      currentUserRef={currentUserRef}
    />
  );
};
